import React, { useCallback, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import { rem } from 'design-system/values';
import { CardView } from 'design-system/CardView';
import IconLineUp from 'design-system/icons/IconLineUp';
import SectionHeader from 'design-system/SectionHeader';
import IconLineChart from 'design-system/icons/IconLineChart';
import IconCandleChart from 'design-system/icons/IconCandleChart';
import { Button, SettingsItemContent, SettingsItemDescription, SettingsItemSwitch, SettingsItemTitle, Spacer, ToolTip, } from 'design-system';
import ChartTypeSelectRow from './ChartTypeSelectRow';
const strings = {
    lineTooltipContent: `This price chart provides the asset’s daily close prices for the selected time period, displaying as a continuous line.\n\nThe highest and lowest prices of the selected time period are indicated on the graph, as well as the price at the beginning of the selected time period on the left (which is highlighted by a horizontal dotted line).`,
    candleTooltipContent: 'This price chart provides information on price movements by showing five data points (open, close, price change, high, low, and volume) for each time interval represented by the candles.\n\nIf the price has increased (i.e. the close price is above the open price) the candle is blue/green. If the price has decreased (i.e. the close price is below the open price) the candle is pink/red.\n\nThe y-axis of the chart provides the highest and lowest prices of the selected time period on the right, as well as the price at the beginning of the selected time period on the left (which is highlighted by a horizontal dotted line).',
    buyAndSellTooltipContent: 'This setting indicates your buy and sells on the graph.',
};
export const StockChartSettingsModalContent = ({ onSave, chartType, shouldShowBuyAndSell, }) => {
    const [showBuyAndSell, setShowBuyAndSell] = useState(shouldShowBuyAndSell);
    const [chart, setChart] = useState(chartType);
    const handleSelect = useCallback((chartType) => () => {
        setChart(chartType);
    }, []);
    const onValueChange = useCallback((newValue) => {
        setShowBuyAndSell(newValue);
    }, []);
    const handleSave = useCallback(() => {
        Modal.hide(() => {
            onSave(chart, showBuyAndSell);
        });
    }, [chart, onSave, showBuyAndSell]);
    return (<View style={styles.contentContainer}>
      <Text Text-24>Chart settings</Text>
      <SectionHeader largeVerticalMargins>Chart types</SectionHeader>
      <CardView noMargin>
        <ChartTypeSelectRow title="Line chart" icon={<IconLineChart />} isSelected={chart === 'line'} onSelect={handleSelect('line')} toolTipDesc={strings.lineTooltipContent}/>
        <ChartTypeSelectRow title="Candlestick" icon={<IconCandleChart />} isSelected={chart === 'candle'} onSelect={handleSelect('candle')} toolTipDesc={strings.candleTooltipContent}/>
      </CardView>
      <SectionHeader largeVerticalMargins>Indicators</SectionHeader>
      <CardView noMarginTop noMarginBottom noMargin>
        <SettingsItemSwitch icon={<IconLineUp />} value={showBuyAndSell} onValueChange={onValueChange}>
          <SettingsItemContent>
            <View style={styles.row}>
              <SettingsItemTitle>Buys and sells</SettingsItemTitle>
              <Spacer w={8}/>
              <ToolTip title="Buy and sells" subtitle={strings.buyAndSellTooltipContent}/>
            </View>
            <SettingsItemDescription>For line chart only</SettingsItemDescription>
          </SettingsItemContent>
        </SettingsItemSwitch>
      </CardView>
      <Spacer h={32}/>
      <Button brand title="Save" onPress={handleSave}/>
    </View>);
};
export const StockChartSettingsModal = {
    show: (chartType, shouldShowBuyAndSell, onSave) => {
        Modal.show(<StockChartSettingsModalContent onSave={onSave} chartType={chartType} shouldShowBuyAndSell={shouldShowBuyAndSell}/>, {
            darkBg: true,
            containerStyle: styles.modal,
        });
    },
    hide: () => {
        Modal.hide();
    },
};
const styles = StyleSheet.create({
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    modal: {
        paddingHorizontal: 0,
        overflow: 'hidden',
    },
    contentContainer: {
        paddingHorizontal: rem(16),
        paddingTop: rem(32),
    },
});
